import type { AppImageProps } from '@elara/client-core/ui-contracts/app-image';

import React from 'react';

import { renditionCandidates } from '@elara/client-core/lib/image-renditions';

import { useFirstAvailableImage } from '#lib/use-first-available-image.js';

export type { AppImageLoading, AppImageProps } from '@elara/client-core/ui-contracts/app-image';

/**
 * Web props for {@link AppImage}: the neutral {@link AppImageProps} contract plus the rest
 * of the DOM `<img>` surface (`style`, `width`/`height`, `decoding`, `fetchPriority`,
 * `onClick`, `srcSet`, …) that web call sites already rely on. The future RN component
 * extends the same neutral contract with its own platform props instead.
 */
export type AppImageWebProps = AppImageProps & Omit<React.ComponentPropsWithoutRef<'img'>, keyof AppImageProps>;

/**
 * The web image seam (ELARA-703). Renders a DOM `<img>` and applies the shared loading
 * policy — `loading="lazy"` and `decoding="async"` by default — so every image gets
 * consistent, deferred, off-main-thread decoding without each call site repeating the
 * attributes. Either default can be overridden per call site.
 *
 * Prefer this over a raw `<img>`. The React Native equivalent (built on `expo-image` with
 * a disk/memory cache policy) will implement the same {@link AppImageProps} contract, so
 * the cross-platform swap stays a single component.
 *
 * @example
 * <AppImage src={hotel.imageUrl} alt={hotel.name} className="h-14 w-14 rounded-sm object-cover" />
 */
export const AppImage = React.forwardRef<HTMLImageElement, AppImageWebProps>(function AppImage(
  { src, alt, loading = 'lazy', decoding = 'async', onError, onAllFailed, displayWidth, ...rest },
  ref,
) {
  // `src` may be a list of candidates, in which case a failure steps to the next one. `key` forces a
  // fresh element per candidate: without it React reuses the `<img>`, the browser never re-requests,
  // and `onError` never fires again — the image sticks on the broken one instead of stepping past it.
  // Each photo is fetched as a rendition sized to `displayWidth` (or capped at 2048px), its original kept
  // as the fallback: a supplier's 6016px original locked the Rooms grid up (see `image-renditions.ts`).
  const pixelRatio = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  const candidates = renditionCandidates(src, displayWidth ? displayWidth * pixelRatio : undefined);
  const { src: currentSrc, onError: step } = useFirstAvailableImage(candidates);
  const lastCandidate = candidates.at(-1);

  return (
    <img
      ref={ref}
      key={currentSrc}
      src={currentSrc}
      alt={alt}
      loading={loading}
      decoding={decoding}
      onError={() => {
        step();
        onError?.();
        if (currentSrc === lastCandidate) onAllFailed?.();
      }}
      {...rest}
    />
  );
});

AppImage.displayName = 'AppImage';
